import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '@shared/api/httpClient';
import { selectableServerImages } from '../utils/serverImageSelection';
import type { ImageCatalog, ImageProfile, ServerImage } from '../types/serverImages';

interface Props {
  profile: ImageProfile;
  value: string;
  minimumDuoVersion?: string;
  onChange: (value: string, image?: ServerImage) => void;
  onResolve?: (image?: ServerImage) => void;
}

export default function ServerImageSelectors({ profile, value, onChange, onResolve, minimumDuoVersion }: Props) {
  const [catalog, setCatalog] = useState<ImageCatalog>({ items: [], defaults: {} });
  const [error, setError] = useState('');
  useEffect(() => {
    let current = true;
    void apiGet<ImageCatalog>('/admin/server-images').then(result => { if (current) setCatalog(result); })
      .catch((reason: unknown) => { if (current) setError(String(reason)); });
    return () => { current = false; };
  }, []);
  const available = selectableServerImages(catalog.items, profile, minimumDuoVersion);
  const defaultImage = available.find(image => image.imageId === catalog.defaults[profile]);
  const selectedImage = value === 'DEFAULT' ? defaultImage : available.find(image => image.imageId === value);
  useEffect(() => { onResolve?.(selectedImage); }, [selectedImage, onResolve]);
  return <div className="override-control form-wide server-image-control">
    <label><span className="field-label-title">{profile === 'duo-proxy' ? 'Duo proxy image' : 'AD / WorkSpaces management host image'}</span>
      <select value={value} onChange={event => {
        const selected = event.target.value;
        onChange(selected, selected === 'DEFAULT' ? defaultImage : available.find(image => image.imageId === selected));
      }}>
        {!value && <option value="" disabled>Keep current configured image</option>}
        <option value="BASE">Public Windows base</option>
        <option value="DEFAULT" disabled={!defaultImage}>Default{defaultImage ? ` · ${defaultImage.name} · ${defaultImage.version}` : ' · no ready default'}</option>
        {value && value !== 'BASE' && value !== 'DEFAULT' && !available.some(image => image.imageId === value) && <option value={value} disabled>Selected image is unavailable</option>}
        {available.map(image => <option key={image.imageId} value={image.imageId}>{image.name} · {image.version}{profile === 'duo-proxy' ? (image.installerEmbedded ? ' · Duo installed' : ' · install Duo at deployment') : ''}</option>)}
      </select>
    </label>
    {error && <span className="error-text" role="alert">{error}</span>}
    <span className="field-help">{profile === 'duo-proxy' ? 'Choose a tested source image. Images without Duo use the approved installer configured below.' : 'Used for temporary Group Policy and CUI file share management hosts.'} Deploy verifies or creates the encrypted account-owned copy before provisioning. <Link to="/server-images" target="_blank">Manage images</Link>.</span>
    {selectedImage && <div className="server-image-selection-summary"><span><strong>Selected:</strong> {selectedImage.name} · {selectedImage.version}</span><span>{profile === 'duo-proxy' ? (selectedImage.installerEmbedded ? `Embedded Duo ${selectedImage.installerVersion || 'version verified by build'}` : 'Duo installation required at deployment') : 'AD and Group Policy tools installed'}</span></div>}
    {value === 'DEFAULT' && <span className="field-help">The ready default is resolved and pinned when you save. Existing selections retain their version.</span>}
  </div>;
}
